import {Component, ChangeDetectorRef, OnInit, OnDestroy} from '@angular/core';
import {Router} from '@angular/router';
import { catchError, takeUntil, switchMap } from 'rxjs/operators';
import {combineLatest, Subject} from 'rxjs';
import {NgxPermissionsService} from 'ngx-permissions';

import {ACCESS_KEYS, AppConfig} from './app.config';
import {SEGMENT} from '@root/app-routing.model';
import {AuthUserModel, AuthUserViewModel} from '@core/user.model';
import {AuthService} from '@core/auth.service';
import {UserService} from '@core/user.service';
import {LocaleService} from '@core/locale.service';
import {DriverCardService} from '@core/driver-card.service';

@Component({
	selector: 'tacho-root',
	templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
	title = 'app-tachomat';

	private firstInit: boolean = true;
	private previousUser: AuthUserModel | null = null;
	private previousCardStatus: boolean = false;
	private previousDlkStatus: boolean = false;

	public transLoaded: boolean = false;
	public showLoader: boolean = false;

	private onDestroy$: Subject<null> = new Subject();
	private onReset$: Subject<null> = new Subject();

	constructor(
		private readonly router: Router,
		private readonly permissionsService: NgxPermissionsService,
		private readonly changeDetector: ChangeDetectorRef,
		private readonly appConfig: AppConfig,
		private readonly authService: AuthService,
		private readonly localeService: LocaleService,
		private readonly userService: UserService,
		public readonly cardService: DriverCardService
	) {
		this.initializeExtras();
	}

	ngOnInit(): void {
		combineLatest(
			this.authService.loggedUser$,
			this.cardService.cardExisting$,
			this.cardService.dlkStatus$
		)
			.pipe(
				takeUntil(this.onDestroy$)
			)
			.subscribe(resp => {
				this.handleUserNavigation(resp);
			});
	}

	ngOnDestroy(): void {
		this.onReset$.next();
		this.onDestroy$.next();
	}

	private handleUserNavigation(data: [AuthUserViewModel | null, boolean, boolean]) {
		const [user, cardStatus, dlkStatus] = data;

		if (this.previousUser && !user) {
			if (cardStatus) {
				this.router.navigate([SEGMENT.REMOVE_CARD]);
			}
		} else if (this.previousDlkStatus && !dlkStatus) {
			if (!cardStatus) {
				this.redirectToGoodbyePage();
			}
		} else if (this.previousCardStatus && !cardStatus) {
			this.logoutUser(user);
			if (dlkStatus) {
				this.router.navigate([SEGMENT.REMOVE_DLK]);
			} else {
				this.redirectToGoodbyePage();
			}

		} else if (!this.previousCardStatus && cardStatus) {
			this.refreshTachomatApp();
		}

		this.previousUser = user;
		this.previousCardStatus = cardStatus;
		this.previousDlkStatus = dlkStatus;
	}

	private resetAndRedirect(tachomatURL: string) {
		this.onReset$.next();
		this.router.navigate([tachomatURL]);
	}

	private refreshTachomatApp() {
		if (!this.firstInit) {
			this.showLoader = false;
			this.resetAndRedirect(`/${SEGMENT.CONNECT}`);
			this.initializeExtras();
		}
		this.firstInit = false;
	}

	private redirectToGoodbyePage() {
		this.resetAndRedirect(`${SEGMENT.GOODBYE_PAGE}`);
	}

	private logoutUser(user: AuthUserViewModel | null) {
		if (user) {
			this.authService.logout()
				.pipe(takeUntil(this.onDestroy$))
				.subscribe();
		}
	}

	private initializeExtras(): void {
		const language: string = this.localeService.manuallyChangedLanguage || this.appConfig.getOSLang();
		this.changeDetector.detach();
		this.cardService.getLanguagesList().pipe(
			switchMap((availableLangs: string[]) => {
				return this.localeService.setLang(availableLangs.includes(language) ? language : this.appConfig.getDefaultLang());
			}),
			takeUntil(this.onReset$),
			catchError(() => {
				const defaultLang: string = this.appConfig.getDefaultLang();
				return this.localeService.setLang(defaultLang);
			})
		).subscribe(
			() => {
				this.transLoaded = true;
				this.changeDetector.reattach();
			}
		);

		this.userService.permissions.pipe(
			takeUntil(this.onReset$)
		).subscribe(permissions => {
			this.permissionsService.loadPermissions(permissions);
			localStorage.setItem(ACCESS_KEYS, JSON.stringify(permissions));
		});
	}
}
